import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatGs, LOCATION_NAMES } from "@/lib/format";
import { PageHeader, StatCard } from "@/components/ui/kit";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";
import { CashHistory } from "@/components/reports/CashHistory";
import { ZReport } from "@/components/reports/ZReport";
import { CashierSummary } from "@/components/reports/CashierSummary";
import { CashierRanking } from "@/components/reports/CashierRanking";
import { CashierSales } from "@/components/reports/CashierSales";
import { TrendingUp, TrendingDown, Wallet, Receipt, Trophy } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function Reports() {
  const { location } = useAuth();
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [allLoc, setAllLoc] = useState(false);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState([]);
  const [cashHist, setCashHist] = useState(null);
  const [byCashier, setByCashier] = useState([]);
  const [cashier, setCashier] = useState("");

  const load = async () => {
    const params = {};
    if (!allLoc) params.location = location;
    if (frm) params.frm = frm;
    if (to) params.to = to;
    const cp = cashier ? { ...params, cashier } : params;
    try {
      const [s, t, tp, ch, bc] = await Promise.all([
        api.get("/reports/summary", { params: cp }),
        api.get("/reports/timeseries", { params: cp }),
        api.get("/reports/top-products", { params: cp }),
        api.get("/reports/cash-sessions", { params }),
        api.get("/reports/by-cashier", { params }),
      ]);
      setSummary(s.data); setSeries(t.data); setTop(tp.data); setCashHist(ch.data); setByCashier(bc.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, [location, frm, to, allLoc, cashier]);
  const cashierName = byCashier.find((c) => c.cashier_id === cashier)?.name;

  const maxTop = Math.max(1, ...top.map((t) => t.quantity));
  const chartColor = "hsl(var(--primary))";

  const tooltipStyle = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 };

  return (
    <div>
      <PageHeader title="Reportes" subtitle={`${allLoc ? "Ambos locales" : `Radiografía · ${LOCATION_NAMES[location]}`}${cashierName ? ` · Cajero: ${cashierName}` : ""}`}>
        <select value={cashier} onChange={(e) => setCashier(e.target.value)} data-testid="report-cashier" className="h-10 px-3 rounded-lg bg-secondary border border-border text-sm">
          <option value="">Todos los cajeros</option>
          {byCashier.map((c) => <option key={c.cashier_id} value={c.cashier_id}>{c.name}</option>)}
        </select>
        <button onClick={() => setAllLoc(!allLoc)} data-testid="toggle-all-loc" className={`h-10 px-4 rounded-lg text-sm font-semibold border transition-colors ${allLoc ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Ambos locales</button>
        <DatePicker value={frm} onChange={setFrm} placeholder="Desde" testId="report-from" />
        <DatePicker value={to} onChange={setTo} placeholder="Hasta" testId="report-to" />
      </PageHeader>

      <CashierRanking location={allLoc ? null : location} refresh={byCashier} />
      <CashierSummary rows={byCashier} selected={cashier} onSelect={setCashier} />
      {cashier && <CashierSales cashierId={cashier} name={cashierName} params={{ ...(allLoc ? {} : { location }), ...(frm ? { frm } : {}), ...(to ? { to } : {}) }} onChanged={load} />}

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Ingresos" value={formatGs(summary.ingresos)} icon={TrendingUp} accent="text-emerald-500" sub={`${summary.num_ventas} ventas`} />
          <StatCard label="Egresos" value={formatGs(summary.egresos)} icon={TrendingDown} accent="text-red-500" sub={cashier ? "del local (no por cajero)" : "compras + gastos"} />
          <StatCard label="Ganancia Neta" value={formatGs(summary.ganancia)} icon={Wallet} accent={summary.ganancia >= 0 ? "text-primary" : "text-red-500"} sub={cashier ? "ventas del cajero − egresos del local" : undefined} />
          <StatCard label="Ticket Promedio" value={formatGs(summary.ticket_promedio)} icon={Receipt} accent="text-amber-500" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-surface p-5">
          <h3 className="font-head font-bold mb-4">Ingresos vs Egresos</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={series} margin={{ left: -10 }}>
              <defs>
                <linearGradient id="gi" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient>
                <linearGradient id="ge" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--destructive))" stopOpacity={0.4} /><stop offset="100%" stopColor="hsl(var(--destructive))" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => (v / 1000) + "k"} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatGs(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="ingresos" name="Ingresos" stroke="hsl(var(--primary))" fill="url(#gi)" strokeWidth={2} />
              <Area type="monotone" dataKey="egresos" name="Egresos" stroke="hsl(var(--destructive))" fill="url(#ge)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card-surface p-5">
          <h3 className="font-head font-bold mb-4 flex items-center gap-2"><Trophy className="w-4 h-4 text-primary" /> Más vendidos</h3>
          <div className="space-y-3" data-testid="top-products">
            {top.map((t, i) => (
              <div key={t.name}>
                <div className="flex justify-between text-sm mb-1"><span className="font-medium truncate">{i + 1}. {t.name}</span><span className="font-data text-muted-foreground">{t.quantity}u</span></div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(t.quantity / maxTop) * 100}%` }} /></div>
              </div>
            ))}
            {top.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Sin ventas en el período</p>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="card-surface p-5">
          <h3 className="font-head font-bold mb-4">Movimiento diario</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={series} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => (v / 1000) + "k"} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatGs(v)} />
              <Bar dataKey="ingresos" name="Ingresos" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="egresos" name="Egresos" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-surface p-5">
          <h3 className="font-head font-bold mb-4">Desglose de gastos</h3>
          {summary && Object.keys(summary.gastos_por_tipo).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(summary.gastos_por_tipo).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between p-3 rounded-lg bg-secondary">
                  <span className="capitalize font-medium text-sm">{k}</span>
                  <span className="font-data font-bold">{formatGs(v)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/20">
                <span className="font-semibold text-sm">Compras de mercadería</span>
                <span className="font-data font-bold text-primary">{formatGs(summary.compras)}</span>
              </div>
            </div>
          ) : <p className="text-sm text-muted-foreground text-center py-6">Sin gastos en el período</p>}
        </div>
      </div>

      <ZReport location={location} />
      <CashHistory data={cashHist} />
    </div>
  );
}
